<template>
    <div class="min-h-app container mx-auto py-8 md:py-16 flex flex-col justify-center">
        <div>
            <Info v-if="messages" :closeable="true" v-model:data="messages" />

            <div
                v-if="showWheelPendingNotice"
                class="relative mx-auto mb-8 md:mb-10 w-full max-w-[814px] rounded-[20px] p-5 md:p-[30px] flex flex-col gap-5 md:gap-[30px] overflow-hidden bg-[#ffcece] border border-[rgba(170,36,36,0.5)]"
            >
                <div class="flex items-center gap-2 w-full">
                    <div class="w-[42px] md:w-[50px] h-[42px] md:h-[50px] flex items-center justify-center shrink-0">
                        <i class="fi fi-rr-hourglass-end text-[#aa2424]" style="font-size: 38px; line-height: 1" aria-hidden="true"></i>
                    </div>
                    <h2
                        class="flex-1 font-semibold leading-[130%] text-[#aa2424]"
                        style="font-family: 'Montserrat', sans-serif; font-size: clamp(26px, 4.2vw, 30px)"
                    >
                        HKCM-Glücksrad 2026
                    </h2>
                </div>

                <p
                    class="max-w-[579px] lg:pr-6 font-normal leading-[130%] text-black"
                    style="font-family: 'SF Pro', 'Montserrat', sans-serif; font-size: clamp(22px, 5vw, 30px)"
                >
                    <strong>STOPP!</strong> Ihr Bezahlprozess ist noch nicht abgeschlossen. Bitte warten Sie einen Moment, damit Sie gleich am Glücksrad teilnehmen können!
                </p>

                <img
                    :src="$asset('/hkcm/images/Phantom_StopSign_Pending.png', 'public')"
                    alt=""
                    aria-hidden="true"
                    class="absolute right-3 md:right-5 top-6 md:top-8 w-[160px] h-[224px] md:w-[189px] md:h-[265px] object-contain pointer-events-none hidden lg:block"
                />
            </div>

            <div class="text-center">
                <div class="loader">
                    <svg class="circular" viewBox="25 25 50 50">
                        <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miterlimit="10" />
                    </svg>
                </div>

                <h1 class="mt-8">
                    {{ $t('order_being_processed') }}
                </h1>
                <p class="mt-8">
                    {{ $t('wait_until_order_complete') }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '../../api';
import { getRegion } from '../../helpers.js';

export default {
    data: () => ({
        messages: null,
        timeout: 8000,
        interval: 10000,
        timeoutFunction: null,
        intervalFunction: null,
        maxTries: 30,
        currentCount: 0,
        orderNumber: null,
        channel: null
    }),

    async mounted() {
        if (window.meta.messages) {
            this.messages = window.meta.messages;
        }

        if (window.meta.authenticated) {
            await this.$store.dispatch('user/user'), await this.$store.dispatch('cart/index');
        }

        this.channel = await this.registerWebsockets();

        await api.post(`/checkout/process/${this.getQueryParameterByName('user_payment_method_id')}`).then((response) => {
            this.orderNumber = response.data.orderNumber;
        });

        this.registerListener();
    },

    unmounted() {
        this.unRegisterServiceWorker();
        this.unRegisterListener();
    },

    methods: {
        async onSuccessWithOrder(orderData) {
            try {
                const payload = this.makePurchasePayloadFromOrder(orderData);
                this.savePurchasePayload(payload); // -> localStorage/sessionStorage
            } catch(e) {
                console.warn('[GA] building payload from order failed', e);
            }

            // NICHTS aus Storage löschen!
            // Optional: cart/index (erst NACH dem Speichern, damit Items nicht verschwinden)
            await this.$store.dispatch('cart/index');
            window.location.href = '/checkout/success';
        },

        // Websocket:
        async registerWebsockets() {
            return await this.$echo
                .private(`App.Models.User.${this.user.id}`)
                .listen('CheckoutFinished', async () => {
                    const order = this.orderNumber ? (await this.loadOrderDetails(this.orderNumber)) : null;
                    await this.onSuccessWithOrder(order || null);
                })
                .listen('CheckoutFailed', async () => {
                    await this.$store.dispatch('cart/index');
                    this.$router.push('/checkout/failed');
                });
        },

        unRegisterServiceWorker() {
            this?.channel?.stopListening('CheckoutFinished');
            this?.channel?.stopListening('CheckoutFailed');
        },

        // Polling:
        registerListener() {
            this.timeoutFunction = setTimeout(() => {
                this.intervalFunction = setInterval(() => {
                    if (this.currentCount <= this.maxTries) {
                        this.$store.dispatch('order/show', this.orderNumber).then(async (response) => {
                            this.currentCount += 1;
                            if (response?.data?.status === 'complete') {
                                await this.onSuccessWithOrder(response.data);
                            } else if (response?.data?.status === 'failed') {
                                await this.$store.dispatch('cart/index');
                                this.$router.push('/checkout/failed');
                            }
                        });
                    } else {
                        this.$router.push('/checkout/failed');
                    }
                }, this.interval);
            }, this.timeout);
        },

        unRegisterListener() {
            clearTimeout(this.timeoutFunction);
            clearInterval(this.intervalFunction);
        }
    },

    computed: {
        ...mapGetters('cart', {
            cart: 'get'
        }),
        ...mapGetters('user', {
            user: 'get'
        }),
        wheel() {
            return this.cart?.wheel || null;
        },
        region() {
            return getRegion();
        },
        isWheelActive() {
            if (this.region !== 'de' || this.wheel?.active !== true || !this.wheel?.start || !this.wheel?.end) return false;

            const now = new Date();
            const start = new Date(this.wheel.start);
            const end = new Date(this.wheel.end);

            return start <= now && now <= end;
        },
        isWheelEligible() {
            return Number(this.cart?.total_end_price_gross || 0) >= Number(this.wheel?.minOrderValue || 0);
        },
        showWheelPendingNotice() {
            return this.isWheelActive && this.isWheelEligible;
        }
    }
};
</script>

<style>
.loader {
    position: relative;
    margin: 0px auto;
    width: 122px;
}

.loader:before {
    content: '';
    display: block;
    padding-top: 100%;
}

.circular {
    -webkit-animation: rotate 2s linear infinite;
    animation: rotate 2s linear infinite;
    height: 100%;
    -webkit-transform-origin: center center;
    -ms-transform-origin: center center;
    transform-origin: center center;
    width: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
}

.path {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
    -webkit-animation:
        dash 1.5s ease-in-out infinite,
        color 6s ease-in-out infinite;
    animation:
        dash 1.5s ease-in-out infinite,
        color 6s ease-in-out infinite;
    stroke-linecap: round;
}

@-webkit-keyframes rotate {
    100% {
        -webkit-transform: rotate(360deg);
        transform: rotate(360deg);
    }
}

@keyframes rotate {
    100% {
        -webkit-transform: rotate(360deg);
        transform: rotate(360deg);
    }
}

@-webkit-keyframes dash {
    0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -35;
    }
    100% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -124;
    }
}

@keyframes dash {
    0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -35;
    }
    100% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -124;
    }
}

@-webkit-keyframes color {
    100%,
    0% {
        stroke: #353cee;
    }
    40% {
        stroke: #353cee;
    }
    66% {
        stroke: #353cee;
    }
    80%,
    90% {
        stroke: #353cee;
    }
}

@keyframes color {
    100%,
    0% {
        stroke: #353cee;
    }
    40% {
        stroke: #353cee;
    }
    66% {
        stroke: #353cee;
    }
    80%,
    90% {
        stroke: #353cee;
    }
}
</style>
